import { useMemo, useRef } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import type { ConnectedContentInlineModel } from 'autogen/swagger/all';
import { ReadMore } from 'autogen/translation-keys/trans-website-shared';
import ContentCard from 'components/ContentCard';
import { shouldLazyLoadImage } from 'components/connectedContent/shared/helpers';
import { mapResponsiveImageModelToImageProps } from 'components/generic/Image/helpers/mapToImageProps';
import QuickViewArticle from 'components/QuickViewArticle/QuickViewArticle';
import { useQuickView } from 'components/quickView';
import {
    guardIsPageActionOfTypeCustomAction,
    guardIsPageActionOfTypeLinkAction
} from 'components/shared/Routing/components/shared/helpers';
import useAdformImpressionTracking from 'hooks/tracking/useAdformImpressionTracking';
import { postAdformClickTracking } from 'modules/tracking/adformClickTracking';

const StyledContentCard = styled(ContentCard)<{ $spanTwo: boolean; $isPreview: boolean }>`
    min-height: 388px;

    ${({ $spanTwo }) =>
        $spanTwo &&
        css`
            grid-column: span 2;
        `};

    ${({ $isPreview }) =>
        $isPreview &&
        css`
            min-height: 425px;
            height: 1px;
        `};
`;

function InlineConnectedContent({ model }: { model: ConnectedContentInlineModel }) {
    let { isQuickView } = model;
    const {
        image,
        link,
        heading,
        text,
        widthType,
        contentLabel,
        adformTracking,
        dataLayer,
        placement,
        isPreview,
        designThemeNameOverride
    } = model;
    const imageProps = useMemo(
        () => mapResponsiveImageModelToImageProps({ ...image, lazyLoad: shouldLazyLoadImage(image, placement) }),
        [image, placement]
    );
    const pageActionLink = link?.pageAction ? guardIsPageActionOfTypeLinkAction(link?.pageAction) : undefined;
    const isCustomLinkAction = link?.pageAction ? !!guardIsPageActionOfTypeCustomAction(link.pageAction) : false;
    isQuickView = isQuickView && !isCustomLinkAction;
    const tags = contentLabel ? [contentLabel] : undefined;
    const ref = useRef<HTMLImageElement | null>(null);
    const identifier = `placement-${placement}_size-${widthType}`;
    const { toggle } = useQuickView(identifier);
    const { t } = useTranslation();

    function handleOpenQuickView() {
        toggle(true);

        if (adformTracking?.clickTracking) {
            postAdformClickTracking(adformTracking.clickTracking);
        }
    }

    useAdformImpressionTracking({
        ref: ref,
        impTrackingUrls: adformTracking?.impTrackers
    });

    return (
        <>
            {pageActionLink && isQuickView && (
                <QuickViewArticle quickViewId={identifier} linkAction={pageActionLink.action} />
            )}

            <StyledContentCard
                ref={ref}
                $spanTwo={widthType === 'Two'}
                $isPreview={isPreview}
                image={{
                    ...imageProps,
                    aspectRatio: 'fill',
                    sizes: widthType === 'Two' ? '500px' : undefined
                }}
                link={link?.pageAction && !isQuickView ? { action: link.pageAction } : undefined}
                heading={text}
                subheading={heading}
                tags={tags}
                data-datalayer={JSON.stringify(dataLayer)}
                actions={
                    pageActionLink && isQuickView
                        ? [{ onClick: handleOpenQuickView, label: t(ReadMore), variant: 'outline' }]
                        : undefined
                }
                type={designThemeNameOverride}
            />
        </>
    );
}

export default withErrorBoundary(InlineConnectedContent, {
    fallback: null,
    onError(error, info) {
        console.error(error, info);
    }
});
